import React, { useState, useEffect } from 'react';
import {
  Globe,
  Link,
  Copy,
  Check,
  QrCode,
  Eye,
  EyeOff,
  Type,
  Clock,
  AlertCircle,
  ExternalLink,
  Download,
  Code,
  RefreshCw,
  Sparkles,
  CheckCircle,
  Info,
  Phone,
  Shield,
  MapPin
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../services/api';

// ============================================================================
// ONLINE BOOKING SETTINGS TAB
// Manages public lead form configuration
// ============================================================================

// Reusable Toggle Switch Component with better visibility
const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <label className="relative inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="sr-only peer"
    />
    <div className={`
      w-12 h-6 rounded-full transition-colors duration-200
      ${checked ? 'bg-primary-600' : 'bg-gray-300'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      peer-focus:ring-4 peer-focus:ring-primary-100
      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
      after:bg-white after:border after:border-gray-200 after:rounded-full
      after:h-5 after:w-5 after:shadow-sm after:transition-transform after:duration-200
      ${checked ? 'after:translate-x-6' : 'after:translate-x-0'}
    `} />
  </label>
);

const OnlineBookingTab = () => {
  // -------------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------------
  
  const [settings, setSettings] = useState({
    publicSlug: '',
    acceptOnlineLeads: false,
    brandColor: '#3B82F6',
    leadFormHeadline: '',
    leadFormThankYouMessage: '',
    leadResponseTimeHours: 24,
    // Quote form header branding
    tagline: '',
    trustBadges: '',
    showPhoneOnLeadForm: true,
    showServiceArea: true,
  });
  
  const [originalSettings, setOriginalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Slug validation
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [slugError, setSlugError] = useState(null);
  
  // Copy states
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  
  // QR Code
  const [qrSize, setQrSize] = useState(200);

  // -------------------------------------------------------------------------
  // URLs
  // -------------------------------------------------------------------------
  
  const APP_URL = import.meta.env.VITE_APP_URL || 'https://suddyflow.com';

  // -------------------------------------------------------------------------
  // LOAD SETTINGS
  // -------------------------------------------------------------------------
  
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get('/business/settings');
      const data = response.data;
      
      const loadedSettings = {
        publicSlug: data.publicSlug || '',
        acceptOnlineLeads: data.acceptOnlineLeads || false,
        brandColor: data.brandColor || '#3B82F6',
        leadFormHeadline: data.leadFormHeadline || '',
        leadFormThankYouMessage: data.leadFormThankYouMessage || '',
        leadResponseTimeHours: data.leadResponseTimeHours || 24,
        tagline: data.tagline || '',
        trustBadges: data.trustBadges || '',
        showPhoneOnLeadForm: data.showPhoneOnLeadForm ?? true,
        showServiceArea: data.showServiceArea ?? true,
      };
      
      setSettings(loadedSettings);
      setOriginalSettings(loadedSettings);
      
      if (loadedSettings.publicSlug) {
        setSlugAvailable(true);
      }
      
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Failed to load settings. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // SAVE SETTINGS
  // -------------------------------------------------------------------------
  
  const saveSettings = async () => {
    if (settings.publicSlug !== originalSettings?.publicSlug) {
      if (slugAvailable === false) {
        setError('Please choose a different URL - this one is taken.');
        return;
      }
      if (slugError) {
        setError(slugError);
        return;
      }
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      // FIXED: Send empty strings instead of null so backend can clear fields
      await api.put('/business/settings', {
        publicSlug: settings.publicSlug || null,
        acceptOnlineLeads: settings.acceptOnlineLeads,
        leadFormHeadline: settings.leadFormHeadline,           // Send "" to clear
        leadFormThankYouMessage: settings.leadFormThankYouMessage,
        leadResponseTimeHours: settings.leadResponseTimeHours,
        tagline: settings.tagline,                             // Send "" to clear
        trustBadges: settings.trustBadges,                     // Send "" to clear
        showPhoneOnLeadForm: settings.showPhoneOnLeadForm,
        showServiceArea: settings.showServiceArea,
      });
      
      setOriginalSettings(settings);
      setSuccessMessage('Settings saved successfully!');
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // SLUG VALIDATION
  // -------------------------------------------------------------------------
  
  const validateSlugFormat = (slug) => {
    if (!slug) return null;
    if (slug.length < 3) return 'URL must be at least 3 characters';
    if (slug.length > 100) return 'URL must be less than 100 characters';
    if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug) && slug.length > 2) {
      return 'URL can only contain lowercase letters, numbers, and hyphens';
    }
    if (slug.includes('--')) return 'URL cannot contain consecutive hyphens';
    return null;
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug) {
      setSlugError(null);
      setSlugAvailable(null);
      return;
    }
    
    const formatError = validateSlugFormat(slug);
    if (formatError) {
      setSlugError(formatError);
      setSlugAvailable(null);
      return;
    }
    
    if (slug === originalSettings?.publicSlug) {
      setSlugAvailable(true);
      setSlugError(null);
      return;
    }
    
    try {
      setCheckingSlug(true);
      setSlugError(null);
      
      const response = await api.get(`/public/check-slug/${slug}`);
      setSlugAvailable(response.data.available);
      
      if (!response.data.available) {
        setSlugError(response.data.reason || 'This URL is already taken');
      }
      
    } catch (err) {
      console.error('Error checking slug:', err);
      if (err.response?.status === 404) {
        setSlugAvailable(true);
        setSlugError(null);
      } else {
        setSlugError('Unable to verify availability');
        setSlugAvailable(null);
      }
    } finally {
      setCheckingSlug(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (settings.publicSlug && settings.publicSlug !== originalSettings?.publicSlug) {
        checkSlugAvailability(settings.publicSlug);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [settings.publicSlug]);

  // -------------------------------------------------------------------------
  // HELPERS
  // -------------------------------------------------------------------------
  
  const publicUrl = settings.publicSlug 
    ? `${APP_URL}/request-quote/${settings.publicSlug}`
    : null;
    
  const embedCode = settings.publicSlug
    ? `<iframe src="${APP_URL}/embed/quote/${settings.publicSlug}" width="100%" height="900" frameborder="0" style="border: none; border-radius: 16px;"></iframe>`
    : null;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopiedEmbed(true);
        setTimeout(() => setCopiedEmbed(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = qrSize;
      canvas.height = qrSize;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `qr-code-${settings.publicSlug}.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const hasUnsavedChanges = 
    settings.publicSlug !== originalSettings?.publicSlug ||
    settings.acceptOnlineLeads !== originalSettings?.acceptOnlineLeads ||
    settings.leadFormHeadline !== originalSettings?.leadFormHeadline ||
    settings.leadFormThankYouMessage !== originalSettings?.leadFormThankYouMessage ||
    settings.leadResponseTimeHours !== originalSettings?.leadResponseTimeHours ||
    settings.tagline !== originalSettings?.tagline ||
    settings.trustBadges !== originalSettings?.trustBadges ||
    settings.showPhoneOnLeadForm !== originalSettings?.showPhoneOnLeadForm ||
    settings.showServiceArea !== originalSettings?.showServiceArea;

  // -------------------------------------------------------------------------
  // RENDER: LOADING
  // -------------------------------------------------------------------------
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // RENDER: MAIN
  // -------------------------------------------------------------------------
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Globe className="w-6 h-6 text-primary-600" />
            Online Lead Capture
          </h2>
          <p className="text-gray-600 mt-1">
            Let customers request quotes directly from your website or a hosted page
          </p>
        </div>
        
        {settings.acceptOnlineLeads && publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Preview Form
          </a>
        )}
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              settings.acceptOnlineLeads ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              {settings.acceptOnlineLeads ? (
                <Eye className="w-6 h-6 text-green-600" />
              ) : (
                <EyeOff className="w-6 h-6 text-gray-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Accept Online Leads</h3>
              <p className="text-sm text-gray-600">
                {settings.acceptOnlineLeads 
                  ? 'Your public quote form is live and accepting submissions'
                  : 'Enable to start receiving leads from your public form'
                }
              </p>
            </div>
          </div>
          
          <ToggleSwitch
            checked={settings.acceptOnlineLeads}
            onChange={(e) => setSettings(prev => ({ ...prev, acceptOnlineLeads: e.target.checked }))}
          />
        </div>
      </div>

      {/* URL Configuration */}
      <div className="card">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Link className="w-5 h-5 text-primary-600" />
          Your Public Form URL
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom URL Slug
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-gray-500 text-sm whitespace-nowrap">
                {APP_URL}/request-quote/
              </span>
              <div className="relative flex-1">
                <input
                  type="text"
                  value={settings.publicSlug}
                  onChange={(e) => {
                    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    setSettings(prev => ({ ...prev, publicSlug: slug }));
                  }}
                  placeholder="your-business-name"
                  className={`input pr-10 ${
                    slugError ? 'border-red-300 focus:border-red-500' :
                    slugAvailable === true ? 'border-green-300 focus:border-green-500' :
                    ''
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {checkingSlug && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
                  {!checkingSlug && slugAvailable === true && <Check className="w-4 h-4 text-green-500" />}
                  {!checkingSlug && slugAvailable === false && <AlertCircle className="w-4 h-4 text-red-500" />}
                </div>
              </div>
            </div>
            {slugError && (
              <p className="mt-1 text-sm text-red-600">{slugError}</p>
            )}
            {slugAvailable === true && settings.publicSlug !== originalSettings?.publicSlug && (
              <p className="mt-1 text-sm text-green-600">✓ This URL is available!</p>
            )}
          </div>
          
          {publicUrl && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <code className="text-sm text-primary-600 break-all">{publicUrl}</code>
                <button
                  onClick={() => copyToClipboard(publicUrl, 'link')}
                  className="btn btn-secondary text-sm flex items-center gap-1 flex-shrink-0"
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Form Content Customization */}
      <div className="card">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Type className="w-5 h-5 text-primary-600" />
          Form Content & Branding
        </h3>
        
        <div className="space-y-5">
          {/* Tagline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tagline
            </label>
            <input
              type="text"
              value={settings.tagline}
              onChange={(e) => setSettings(prev => ({ ...prev, tagline: e.target.value }))}
              placeholder="Where Bubbles Meet Business"
              className="input"
              maxLength={200}
            />
            <p className="mt-1 text-xs text-gray-500">A memorable phrase shown below your logo</p>
          </div>
          
          {/* Trust Badges */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Shield className="w-4 h-4 inline mr-1" />
              Trust Badges
            </label>
            <input
              type="text"
              value={settings.trustBadges}
              onChange={(e) => setSettings(prev => ({ ...prev, trustBadges: e.target.value }))}
              placeholder="Veteran Owned • Licensed & Insured • 5-Star Rated"
              className="input"
              maxLength={300}
            />
            <p className="mt-1 text-xs text-gray-500">Credentials that build trust — separate with •</p>
          </div>
          
          {/* Headline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Form Headline
            </label>
            <input
              type="text"
              value={settings.leadFormHeadline}
              onChange={(e) => setSettings(prev => ({ ...prev, leadFormHeadline: e.target.value }))}
              placeholder="Get Your Free Quote"
              className="input"
              maxLength={200}
            />
            <p className="mt-1 text-xs text-gray-500">Can leave as blank or add something such as Get Your Free Quote</p>
          </div>
          
          {/* Toggle Options - Better visibility */}
          <div className="space-y-3 pt-2">
            {/* Show Phone Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <Phone className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Show Phone Number</p>
                  <p className="text-sm text-gray-500">Display clickable phone button in header</p>
                </div>
              </div>
              <ToggleSwitch
                checked={settings.showPhoneOnLeadForm}
                onChange={(e) => setSettings(prev => ({ ...prev, showPhoneOnLeadForm: e.target.checked }))}
              />
            </div>
            
            {/* Show Service Area Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg border border-gray-200">
                  <MapPin className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Show Service Area</p>
                  <p className="text-sm text-gray-500">Display "Serving [City], [State] & surrounding areas"</p>
                </div>
              </div>
              <ToggleSwitch
                checked={settings.showServiceArea}
                onChange={(e) => setSettings(prev => ({ ...prev, showServiceArea: e.target.checked }))}
              />
            </div>
          </div>
          
          {/* Response Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Response Time Promise
            </label>
            <select
              value={settings.leadResponseTimeHours}
              onChange={(e) => setSettings(prev => ({ ...prev, leadResponseTimeHours: parseInt(e.target.value) }))}
              className="input"
            >
              <option value={1}>Within 1 hour</option>
              <option value={2}>Within 2 hours</option>
              <option value={4}>Within 4 hours</option>
              <option value={8}>Within 8 hours</option>
              <option value={12}>Within 12 hours</option>
              <option value={24}>Within 24 hours</option>
              <option value={48}>Within 2 business days</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Shown to customers after they submit</p>
          </div>
          
          {/* Thank You Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Sparkles className="w-4 h-4 inline mr-1" />
              Thank You Message
            </label>
            <textarea
              value={settings.leadFormThankYouMessage}
              onChange={(e) => setSettings(prev => ({ ...prev, leadFormThankYouMessage: e.target.value }))}
              placeholder="Thanks! We'll be in touch soon."
              className="input"
              rows={2}
              maxLength={500}
            />
            <p className="mt-1 text-xs text-gray-500">Shown after successful form submission</p>
          </div>
        </div>
        
        {/* Info about logo/color */}
        <div className="mt-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              Your logo and brand color from <strong>Settings → Branding</strong> are automatically applied to the form header.
            </p>
          </div>
        </div>
      </div>

      {/* QR Code Generator */}
      {settings.acceptOnlineLeads && publicUrl && (
        <div className="card">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary-600" />
            QR Code for Print Materials
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex flex-col items-center justify-center p-6 bg-white border-2 border-dashed border-gray-200 rounded-xl">
              <QRCodeSVG
                id="qr-code-svg"
                value={publicUrl}
                size={qrSize}
                level="M"
                includeMargin={true}
                fgColor={settings.brandColor}
              />
              <p className="mt-4 text-sm text-gray-600 text-center">
                Scan to get a quote
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QR Code Size
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[150, 200, 300, 400].map(size => (
                    <button
                      key={size}
                      onClick={() => setQrSize(size)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        qrSize === size
                          ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                          : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                      }`}
                    >
                      {size}px
                    </button>
                  ))}
                </div>
              </div>
              
              <button
                onClick={downloadQRCode}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download QR Code (PNG)
              </button>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>💡 Pro Tip:</strong> Add this QR code to your business cards, truck wraps, yard signs, and door hangers!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Embed Code */}
      {settings.acceptOnlineLeads && embedCode && (
        <div className="card">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Code className="w-5 h-5 text-primary-600" />
            Embed on Your Website
          </h3>
          
          <p className="text-gray-600 mb-4">
            Add this code to your website to embed the quote form directly on any page.
          </p>
          
          <div className="relative">
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
              <code>{embedCode}</code>
            </pre>
            <button
              onClick={() => copyToClipboard(embedCode, 'embed')}
              className="absolute top-2 right-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg flex items-center gap-1 transition-colors"
            >
              {copiedEmbed ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
          
          <div className="mt-4 flex items-start gap-2 text-sm text-gray-600">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Works with any website builder: WordPress, Wix, Squarespace, custom HTML, etc.
              Adjust the <code className="bg-gray-100 px-1 rounded">height</code> value if needed.
            </p>
          </div>
        </div>
      )}

      {/* Not Enabled Message */}
      {!settings.acceptOnlineLeads && (
        <div className="card bg-gray-50 border-2 border-dashed border-gray-300">
          <div className="text-center py-8">
            <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-700 mb-2">Online Lead Capture is Disabled</h3>
            <p className="text-gray-500 mb-4">
              Enable it above to get your public form URL, QR code, and embed code.
            </p>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div>
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              You have unsaved changes
            </span>
          )}
        </div>
        <button
          onClick={saveSettings}
          disabled={saving || !hasUnsavedChanges}
          className="btn btn-primary flex items-center gap-2"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OnlineBookingTab;